import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import SelectableListItem from 'components/Utils/SelectableListItem';
import './style.scss';
import { AddCardModal } from 'components/Payments/CreditCardScreen/view';
import ChargePatientForm from 'components/Payments/ChargePatientForm';
import DefaultModal from 'components/Utils/DefaultModal';
import Icon from 'components/Utils/NewIcon';
import DefaultChip from 'components/Utils/DefaultChip';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import SubscriptionUtils from 'services/SubscriptionUtils';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import ConfirmationButton from '../../Utils/NewDefaultButton/ConfirmationButton';

const CreditCardList = (props) => (
  <div className="credit-card-list">
    <span className="small-title">
      Credit Cards
      <Icon
        src="circle-info"
        showTooltip={true}
        tooltipPlace="right"
        alt="Click on a card to remove it"
        style={{
          display: 'inline-block',
          top: '5px',
          left: '10px',
          position: 'relative',
        }}
        width="20px"
      />
    </span>
    <List>
      <ListItems {...props} />
    </List>
    <div className="buttons">
      <PromoCodeButtons {...props} />
      <AddCreditCard {...props} />
      <ChargePatientButton {...props} />
      <SubscribeButton {...props} />
    </div>
  </div>
);

const PromoCodeButtons = ({ patient }) => {
  const dispatch = useAppDispatch();
  const isLoadingDiscountPromoCode = useIsLoading(api.Payments.applyDiscountPromoCode);
  const isLoadingSend = useIsLoading(api.Payments.sendPromoCodeEmail);
  const isLoadingAdd = useIsLoading(api.Payments.applyPromoCode);

  const subscription = useAppSelector((state) => state.payments.subscriptions[patient.id]);
  const unsubscribed = SubscriptionUtils.isUnsubscribed(subscription);

  async function applyDiscountPromoCode() {
    await dispatch(api.Payments.applyDiscountPromoCode({ patientId: patient.id }), {
      title: 'Promo Code',
      successText: "Promo Code has been added to the patient's account",
    });
  }

  async function onSendPromoCode() {
    await dispatch(api.Payments.sendPromoCodeEmail({ patientId: patient.id }), {
      title: 'Promo Code',
      successText: 'Promo Code email scheduled to be sent in the next 5 minutes',
    });
  }

  async function applyPromoCode(promoCode) {
    if (promoCode) {
      await dispatch(api.Payments.applyPromoCode({ patientId: patient.id, promoCode }), {
        title: 'Promo Code',
        successText: "Promo Code added to the patient's account",
      });
      await dispatch(api.Patients.getPatient({ id: patient.id }));
    }
  }

  return (
    <>
      <div className="button-wrapper add-credit-card">
        <ConfirmationButton
          name="Apply Specific Promo Code"
          icon="percent"
          onClick={(e, form) => applyPromoCode(form['Promo Code'])}
          loading={isLoadingAdd}
          confirmation={{
            message: "This will apply the promo code directly to the patient's account.",
            form: [{ type: 'textField', label: 'Promo Code' }],
          }}
        />
      </div>
      {unsubscribed && (
        <div className="row">
          <div className="button-wrapper add-credit-card">
            <ConfirmationButton
              name="Apply PPU Promo"
              icon="percent"
              onClick={applyDiscountPromoCode}
              loading={isLoadingDiscountPromoCode}
              confirmation={{
                message:
                  "This will apply the subscription promo code directly to the patient's account.",
              }}
            />
          </div>
          <div className="button-wrapper add-credit-card">
            <ConfirmationButton
              name="Send PPU Promo"
              icon="envelope"
              onClick={onSendPromoCode}
              loading={isLoadingSend}
              confirmation={{
                message: "This will send a subscription promo code to the patient's email.",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

const AddCreditCard = (props) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <div className="button-wrapper add-credit-card">
      <AddCardModal
        showModal={showModal}
        onAddSuccess={() => setShowModal(false)}
        patient={props.patient}
        openModal={setShowModal}
      />
      <DefaultButton name="Add Credit Card" icon="credit-card" onClick={() => setShowModal(true)} />
    </div>
  );
};

const SubscribeButton = ({ patient, creditCards }) => {
  const dispatch = useAppDispatch();
  const isUnsubscribing = useIsLoading(api.Payments.unsubscribe);
  const loadingRetryPayment = useIsLoading(api.Payments.retryPayment);
  const [showModal, setShowModal] = useState(false);

  const subscription = useAppSelector((state) => state.payments.subscriptions[patient.id]);
  const unsubscribed = SubscriptionUtils.isUnsubscribed(subscription);
  const isOverdue = SubscriptionUtils.isPaymentOverdue(subscription);

  async function onCancel() {
    await dispatch(api.Payments.unsubscribe({ userId: patient.id }), {
      title: 'Cancel Subscription',
      successText: 'Subscription cancelled successfully',
    });
  }

  async function onRetryPayment() {
    await dispatch(api.Payments.retryPayment({ patientId: patient.id }), {
      title: 'Retry Payment',
      successText: 'Payment successful!',
    });
  }

  return (
    <>
      <div className="button-wrapper subscribe-patient">
        <ConfirmationButton
          name="Cancel Subscription"
          hide={unsubscribed}
          onClick={onCancel}
          loading={isUnsubscribing}
          icon="xmark"
          confirmation={{
            message:
              "Are you sure you want to cancel this patient's subscription? It will cancel at the end of the current billing period.",
          }}
        />
      </div>
      <div className="button-wrapper subscribe-patient">
        {!unsubscribed && isOverdue && (
          <DefaultButton
            name="Retry Payment"
            onClick={onRetryPayment}
            loading={loadingRetryPayment}
            icon="arrow-rotate-right"
          />
        )}
        {unsubscribed && (
          <DefaultButton
            name="Add Subscription"
            disabled={!creditCards?.length}
            onClick={() => setShowModal(true)}
            icon="arrow-rotate-right"
          />
        )}
        {showModal && (
          <DefaultModal
            className="modal subscribe-patient-modal fit-modal"
            wrap={true}
            padding={true}
            title="Add Subscription"
            content={<AddSubscription patient={patient} onSuccess={() => setShowModal(false)} />}
            isOpen={showModal}
            onRequestClose={() => setShowModal(false)}
          />
        )}
      </div>
    </>
  );
};

const AddSubscription = ({ patient, onSuccess }) => {
  const dispatch = useAppDispatch();
  const plans = useAppSelector((state) => state.payments.clinic_payment_plans);
  const [plan, setPlan] = useState(null);
  const isLoading = useIsLoading(api.Payments.createSubscription);

  useEffect(() => {
    if (plans.length === 0) dispatch(api.Payments.getAllPaymentPlans());
  }, [dispatch, plans]);

  const filteredPlans = useMemo(() => {
    return plans.filter(
      (plan) => plan.payment_plan?.amount && plan.payment_plan?.interval === 'year',
    );
  }, [plans]);

  async function onSubscribe() {
    let result = await dispatch(
      api.Payments.createSubscription({
        paymentPlanId: plan.id,
        patientId: patient.id,
      }),
      {
        title: 'Subscription',
        successText: 'Subscription created successfully!',
      },
    );

    if (result) {
      onSuccess();
    }
  }

  return (
    <div className="add-subscription">
      <PlanChips
        title="Active Plans"
        plans={filteredPlans.filter(({ payment_plan }) => payment_plan.active)}
        onClick={setPlan}
        activeId={plan?.id}
      />
      <PlanChips
        title="Archived Plans"
        plans={filteredPlans.filter(({ payment_plan }) => !payment_plan.active)}
        onClick={setPlan}
        activeId={plan?.id}
        collapsed={true}
      />

      <div className="button-wrapper">
        <ConfirmationButton
          name={plan ? 'Subscribe to ' + getPlanDescription(plan) : 'Add Subscription'}
          loading={isLoading}
          onClick={onSubscribe}
          disabled={!plan}
          className="blue"
          style={{ width: '100%' }}
          confirmation={{
            message: `This will charge the patient $${SubscriptionUtils.convertPlanAmount(
              plan?.payment_plan?.amount,
            )} and start the ${
              plan?.payment_plan.name
            } subscription. Are you sure you want to proceed?`,
          }}
        />
      </div>
    </div>
  );
};

function getPlanDescription(plan = {}) {
  return `${plan.payment_plan?.name} - $${SubscriptionUtils.convertPlanAmount(
    plan.payment_plan?.amount || 0,
  )}/${plan.payment_plan?.interval}`;
}

const PlanChips = ({ plans = [], title, onClick, activeId, collapsed = false }) => {
  return (
    <UncontrolledCollapse title={title} initialValue={collapsed}>
      <div className="plans">
        {plans.map((plan) => (
          <DefaultChip
            key={plan.id}
            text={getPlanDescription(plan)}
            className={activeId === plan.id ? 'active' : ''}
            onClick={() => onClick(plan)}
          />
        ))}
      </div>
    </UncontrolledCollapse>
  );
};

const ChargePatientButton = (props) => {
  let [showModal, setShowModal] = useState(false);
  return (
    <div className="button-wrapper charge-patient">
      <DefaultButton
        icon="dollar-sign"
        name="Charge Patient"
        disabled={(props.creditCards || []).length === 0}
        onClick={() => setShowModal(true)}
      />
      {showModal && (
        <DefaultModal
          className="modal charge-patient-modal"
          wrap={true}
          padding={true}
          title="Charge Patient"
          content={
            <ChargePatientForm patient={props.patient} onSuccess={() => setShowModal(false)} />
          }
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

const ListItems = ({ creditCards = [], patient }) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState();

  async function deleteCreditCard(card) {
    setLoading(true);
    await dispatch(api.Payments.deleteCreditCard({ patientId: patient.id, cardId: card.id }), {
      errorTitle: 'Credit Card',
    });
    setLoading(false);
  }

  function getBrandIcon(card) {
    let brand = card?.brand?.toLowerCase();
    if (brand === 'american express') brand = 'amex';
    return brand;
  }

  return (
    <>
      {!!confirmDelete && (
        <ConfirmationDialog
          title="Remove Credit Card"
          message="This will remove the credit card. Are you sure you want to proceed?"
          open={!!confirmDelete}
          onConfirm={async () => {
            await deleteCreditCard(confirmDelete);
            setConfirmDelete();
          }}
          onCancel={() => setConfirmDelete()}
          loadingState={loading}
        />
      )}
      {creditCards.map((card, index) => (
        <SelectableListItem
          disabled={loading}
          onClick={() => setConfirmDelete(card)}
          key={index}
          padded={false}
          item={
            <ListItem className="card-item">
              <ListItemAvatar>
                <Icon
                  fontAwesomeStyle="fab"
                  src={`cc-${getBrandIcon(card)}`}
                  width="40px"
                  alt={card.brand}
                />
              </ListItemAvatar>
              <ListItemText
                primary={<span>xxxx xxxx xxxx {card.last4}</span>}
                secondary={
                  <>
                    <span>
                      Exp: {card.exp_month}/{card.exp_year}
                    </span>
                    {isInvalidCard(card) && <InvalidCardBadge />}
                    {card.is_default && <DefaultCardBadge />}
                  </>
                }
              />
            </ListItem>
          }
        />
      ))}
    </>
  );
};

const DEFAULT_BAGE_STYLE = {
  textTransform: 'uppercase',
  float: 'right',
  fontSize: '10px',
};

const DefaultCardBadge = () => (
  <DefaultChip
    text="Default"
    backgroundColor="#63C073"
    textColor="white"
    style={DEFAULT_BAGE_STYLE}
  />
);

const InvalidCardBadge = () => (
  <DefaultChip
    label="Expired"
    backgroundColor="#ff3b5e"
    textColor="white"
    style={DEFAULT_BAGE_STYLE}
  />
);

function isInvalidCard(card) {
  let currentDate = moment();
  let currentYear = currentDate.tz(CalendarUtils.getTargetTimezone()).format('YY');
  let currentMonth = currentDate.tz(CalendarUtils.getTargetTimezone()).format('MM');

  return (
    Number(currentYear) > Number(card.exp_year) ||
    (Number(currentYear) === Number(card.exp_year) &&
      Number(currentMonth) <= Number(card.exp_month))
  );
}

CreditCardList.propTypes = {
  creditCards: PropTypes.array,
};

export default CreditCardList;
